import React from "react";
import { useResponsiveTextContent } from "./use-responsive-text-content";

// Types collocated with implementation
export type FontStyle = "D1" | "D2" | "D3" | "D4" | "D5";

export interface FontConfiguration {
  fontFamily: string;
  fontSizeDesktop: string;
  fontSizeMobile: string;
  lineHeight: string;
  letterSpacing: string;
  fontWeight: string;
  textTransform: string;
}

export interface EditorialTextProps {
  fontStyle: FontStyle; // D1 through D5
  className?: string;
  hideClassNames?: string;
  authoringName?: string;
  rhUid?: string;
  // Responsive text content properties
  textXS?: string;
  textSM?: string;
  textMD?: string;
  textLG?: string;
  textXL?: string;
}

export const EDITORIAL_TEXT_KEY = "rh/components/content/editorial-text";

// Font configuration mapping for D1-D5 styles based on design system
const FONT_CONFIGURATIONS: Record<FontStyle, FontConfiguration> = {
  D1: {
    fontFamily: "var(--font-rhc-hairline)", // RHC Ultra Hairline (weight 100)
    fontSizeDesktop: "10rem", // 120px / 12px = 10rem
    fontSizeMobile: "6.667rem", // 80px / 12px = 6.667rem
    lineHeight: "0.8", // 80%
    letterSpacing: "-0.01em", // -1.2px at 120px
    fontWeight: "100", // Ultra Hairline - using weight 100 from rhcHairline
    textTransform: "uppercase"
  },
  D2: {
    fontFamily: "var(--font-rhc-hairline)", // RHC Hairline (weight 200)
    fontSizeDesktop: "7.5rem", // 90px / 12px = 7.5rem
    fontSizeMobile: "5rem", // 60px / 12px = 5rem
    lineHeight: "0.8", // 80%
    letterSpacing: "-0.01em", // -0.9px at 90px
    fontWeight: "200", // Hairline - using weight 200 from rhcHairline
    textTransform: "uppercase"
  },
  D3: {
    fontFamily: "var(--font-rhc-hairline)", // RHC Hairline (weight 200)
    fontSizeDesktop: "5.833rem", // 70px / 12px = 5.833rem
    fontSizeMobile: "4rem", // 48px / 12px = 4rem
    lineHeight: "0.8", // 80%
    letterSpacing: "-0.01em", // -0.7px at 70px
    fontWeight: "200", // Hairline - using weight 200 from rhcHairline
    textTransform: "uppercase"
  },
  D4: {
    fontFamily: "var(--font-rhc-hairline)", // RHC UltraThin (weight 300)
    fontSizeDesktop: "3.333rem", // 40px / 12px = 3.333rem
    fontSizeMobile: "2.5rem", // 30px / 12px = 2.5rem
    lineHeight: "0.9", // 90%
    letterSpacing: "-0.04em", // -1.6px at 40px
    fontWeight: "300", // UltraThin - using weight 300 from rhcHairline
    textTransform: "uppercase"
  },
  D5: {
    fontFamily: "var(--font-rhc)", // RHC Thin (weight 100)
    fontSizeDesktop: "1.833rem", // 22px / 12px = 1.833rem
    fontSizeMobile: "1.667rem", // 20px / 12px = 1.667rem
    lineHeight: "0.9", // 90%
    letterSpacing: "-0.01em", // -0.22px at 22px
    fontWeight: "100", // Thin - using weight 100 from rhc
    textTransform: "uppercase"
  }
};

const EditorialText: React.FC<EditorialTextProps & Record<string, unknown>> = ({
  fontStyle = "D1",
  className = "",
  hideClassNames = "",
  authoringName,
  rhUid,
  textXS,
  textSM,
  textMD,
  textLG,
  textXL
}) => {
  // Get font configuration for the specified style
  const fontConfig = FONT_CONFIGURATIONS[fontStyle];

  // Create CSS custom properties for responsive typography
  const style = {
    "--font-family": fontConfig.fontFamily,
    "--font-size-desktop": fontConfig.fontSizeDesktop,
    "--font-size-mobile": fontConfig.fontSizeMobile,
    "--line-height": fontConfig.lineHeight,
    "--letter-spacing": fontConfig.letterSpacing,
    "--font-weight": fontConfig.fontWeight,
    "--text-transform": fontConfig.textTransform
  } as React.CSSProperties;

  // Create responsive Tailwind classes using CSS custom properties
  const tailwindClasses = `
    text-responsive-content
    [font-family:var(--font-family)]
    [font-size:var(--font-size-mobile)]
    md:[font-size:var(--font-size-desktop)]
    [line-height:var(--line-height)]
    [letter-spacing:var(--letter-spacing)]
    [font-weight:var(--font-weight)]
    [text-transform:var(--text-transform)]
  `
    .trim()
    .replace(/\s+/g, " ");

  // Use hook only for responsive content logic
  const responsiveContentItems = useResponsiveTextContent({
    textXS,
    textSM,
    textMD,
    textLG,
    textXL
  });

  // Combine the base Tailwind classes with custom and hide classes
  const finalClassName = `${tailwindClasses} ${hideClassNames} ${className}`
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div
      style={style}
      className={finalClassName}
      data-authoring-name={authoringName}
      data-rh-uid={rhUid}
      data-font-style={fontStyle}
    >
      {responsiveContentItems.map(item => (
        <div
          key={item.key}
          className={item.className}
          dangerouslySetInnerHTML={{ __html: item.content }}
        />
      ))}
    </div>
  );
};

export default EditorialText;
